import { Link } from "@tanstack/react-router";
import { Award, Clock3, MapPin, Phone, ShieldCheck, Star } from "lucide-react";
import { useState } from "react";

import type { FaqItem, ServicePage } from "@/data/types";
import { business } from "@/lib/business";
import { WhatsAppButton } from "./WhatsAppChat";

export function CtaBand({ title, text }: { title: string; text: string }) {
  return (
    <section className="bg-primary text-primary-foreground">
      <div className="container-page flex flex-col items-start gap-6 py-12 md:flex-row md:items-center md:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl md:text-3xl">{title}</h2>
          <p className="mt-2 text-sm opacity-90">{text}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <WhatsAppButton variant="gold">Chamar no WhatsApp</WhatsAppButton>
          <a
            href={`tel:${business.phoneRaw}`}
            className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 px-6 py-3 text-sm font-semibold hover:bg-primary-foreground/10"
          >
            <Phone className="h-4 w-4" /> {business.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

export function TrustBar() {
  const items = [
    { icon: Award, title: `Desde ${business.since}`, text: "Meio século de tradição em Betim" },
    { icon: Star, title: `${business.rating} estrelas`, text: `${business.reviewCount} avaliações no Google` },
    { icon: Clock3, title: "Atendimento 24h", text: "Emergências a qualquer hora" },
    { icon: ShieldCheck, title: "Serviço garantido", text: "Técnicos próprios e orçamento antes" },
  ];
  return (
    <section className="border-y border-border bg-muted/40">
      <div className="container-page grid gap-6 py-8 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => (
          <div key={item.title} className="flex items-start gap-3">
            <item.icon className="mt-0.5 h-6 w-6 shrink-0 text-gold" />
            <div>
              <p className="text-sm font-semibold">{item.title}</p>
              <p className="text-sm text-muted-foreground">{item.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function FaqSection({ items, title = "Perguntas frequentes" }: { items: FaqItem[]; title?: string }) {
  return (
    <section className="container-page py-12">
      <h2 className="font-display text-2xl md:text-3xl">{title}</h2>
      <div className="mt-6 divide-y divide-border rounded-xl border border-border bg-card">
        {items.map((item) => (
          <details key={item.q} className="group px-5 py-4">
            <summary className="cursor-pointer list-none text-base font-semibold marker:hidden">
              <h3 className="inline text-base font-semibold">{item.q}</h3>
            </summary>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function PageCard({ page }: { page: ServicePage }) {
  return (
    <Link
      to="/$slug"
      params={{ slug: page.slug }}
      className="group flex h-full flex-col rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-[var(--shadow-soft)]"
    >
      <h3 className="text-base font-semibold group-hover:text-primary">{page.keyword}</h3>
      <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{page.description}</p>
      <span className="mt-4 text-sm font-semibold text-primary">Ver detalhes →</span>
    </Link>
  );
}

export function LocationCard() {
  const [showMap, setShowMap] = useState(false);

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-card-foreground">
        <MapPin className="h-5 w-5 text-gold" /> Nossa loja em Betim
      </h2>
      <p className="mt-2 text-sm font-medium text-card-foreground">
        {business.street} - {business.neighborhood}, {business.city} - {business.region}, {business.postalCode}
      </p>
      <ul className="mt-4 space-y-1 text-sm">
        {business.hours.map((hour) => (
          <li key={hour.day} className="flex justify-between gap-4">
            <span className="font-medium text-card-foreground">{hour.day}</span>
            <span className="font-semibold text-card-foreground">{hour.value}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm font-semibold text-primary">
        Emergências fora do horário: atendimento 24 horas por telefone e WhatsApp.
      </p>
      <div className="mt-4 overflow-hidden rounded-lg border border-border">
        {showMap ? (
          <iframe
            title="Mapa da Chavel Chaveiro em Betim"
            src={business.mapsEmbedUrl}
            width="100%"
            height="240"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="block"
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowMap(true)}
            className="flex h-[240px] w-full flex-col items-center justify-center gap-2 bg-muted text-sm font-semibold text-primary transition-colors hover:bg-secondary"
          >
            <MapPin className="h-6 w-6 text-gold" />
            Ver mapa da loja
            <span className="text-xs font-normal text-muted-foreground">
              {business.street} - {business.neighborhood}, {business.city}
            </span>
          </button>
        )}
      </div>
      <a
        href={business.mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-block text-sm font-semibold text-primary underline"
      >
        Abrir rota no Google Maps
      </a>
    </div>
  );
}
